import { useListState, UseListStateHandlers } from '@mantine/hooks';
import { Outlet, useParams } from 'react-router-dom';
import { createContext, useEffect, useMemo, useState } from 'react';
import { LocationS } from '../LocationS';
import { DistanceDto, LocationDto, LocationWithDistance } from '../../Types/LocationT';
import { useOwnContact } from './ContactContext';

interface Props {
  loading: boolean;
  locations: LocationDto[];
  location: LocationWithDistance | null;
  distance: DistanceDto | null;
  handlers: UseListStateHandlers<LocationDto>;
  handleLocationChange: (patch: Partial<LocationDto>) => void;
  markAsPrimary: (locationId: number) => void;
}

export const EventLocationContext = createContext({} as Props);

// eslint-disable-next-line import/prefer-default-export
export const EventLocationProvider = () => {
  const { id: eventId } = useParams();
  const [loading, setLoading] = useState(false);
  const { contact } = useOwnContact();
  const [distance, setDistance] = useState<DistanceDto | null>(null);
  const [locations, handlers] = useListState<LocationDto>([]);
  const primaryLocation = useMemo(() => locations.find((l) => l?.useAsPrimary), [locations]);
  const { setState, applyWhere } = handlers;
  const { id: contactId } = contact;

  useEffect(() => {
    if (eventId) {
      setLoading(true);
      LocationS.fetchAllByEventId(Number(eventId))
        .then(setState)
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (contactId && primaryLocation && !LocationS.isEmptyLocation(primaryLocation)) {
      LocationS.calculateDistance(contactId, primaryLocation.id).then(setDistance);
    }
    if (primaryLocation && LocationS.isEmptyLocation(primaryLocation) && distance) {
      setDistance(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryLocation, contactId]);

  const markAsPrimary = async (locationId: number) => {
    const edited = await LocationS.editLocation(locationId, { useAsPrimary: true });
    if (edited) {
      const newLocations = locations.map((loc) => ({ ...loc, useAsPrimary: locationId === loc.id }));
      setState(newLocations);
    }
  };

  const handleLocationChange = (patch: Partial<LocationDto>) => {
    applyWhere(
      (loc) => loc?.useAsPrimary === true,
      (loc) => ({ ...loc, ...patch }),
    );
  };

  const value = useMemo(
    () => ({
      loading,
      locations,
      location: primaryLocation
        ? {
            ...primaryLocation,
            // TODO: Get rid of those values once an old placeholders are not used
            distance: distance?.distanceInKiloMeters,
            double_distance: distance?.doubleDistanceInKiloMeters,
            quadruple_distance: distance ? distance.doubleDistanceInKiloMeters * 2 : undefined,
          }
        : null,
      distance,
      handlers,
      handleLocationChange,
      markAsPrimary,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, locations, primaryLocation, distance, handlers],
  );

  return (
    <EventLocationContext.Provider value={value}>
      <Outlet />
    </EventLocationContext.Provider>
  );
};
